import { lazy } from "react"
import { OysterRouter } from "~/router/type"
import { LazyLoadingSuspenseWithErrorBoundary } from "@oysterhr/feature-components"
import FlagProtectedRoute from "~/router/outlets/FlagProtectedRoute"

const LazyAdminInvoicesPage = lazy(() => import("./pages/AdminInvoicesPage/AdminInvoicesPage"))
const LazyAdminCreateInvoicePage = lazy(
  () => import("./pages/AdminCreateInvoicePage/AdminCreateInvoicePage"),
)
const LazyAdminCreateContactorInvoicePage = lazy(
  () => import("./pages/AdminContactorInvoicePages/AdminCreateContactorInvoicePage"),
)
const LazyAdminEditContactorInvoicePage = lazy(
  () => import("./pages/AdminContactorInvoicePages/AdminEditContactorInvoicePage"),
)
const LazyAdminEditInvoicePage = lazy(
  () => import("./pages/AdminEditInvoicePage/AdminEditInvoicePage"),
)
const LazyAdminViewInvoicePage = lazy(
  () => import("./pages/AdminViewInvoicePage/AdminViewInvoicePage"),
)
const LazyAdminInvoicesUploadVendorDataPage = lazy(
  () => import("./pages/AdminInvoicesUploadVendorDataPage/AdminInvoicesUploadVendorDataPage"),
)
const LazyAdminGenerateDepositInvoicePage = lazy(
  () => import("./pages/AdminGenerateDepositInvoicePage/AdminGenerateDepositInvoicePage"),
)
const LazyAdminInvoicesUploadVendorDataItemPage = lazy(
  () =>
    import("./pages/AdminInvoicesUploadVendorDataItemPage/AdminInvoicesUploadVendorDataItemPage"),
)
const LazyAdminAddCreditNotePage = lazy(
  () => import("./pages/AdminAddCreditNotePage/AdminAddCreditNotePage"),
)
const LazyAdminGenerateCreditNotePage = lazy(
  () => import("./pages/AdminGenerateCreditNotePage/AdminGenerateCreditNotePage"),
)
const LazyAdminApplyCreditNotePage = lazy(
  () => import("./pages/AdminApplyCreditNotePage/AdminApplyCreditNotePage"),
)
const LazyAdminActivityLogPage = lazy(
  () => import("./pages/AdminActivityLogPage/AdminActivityLogPage"),
)
const LazyAdminActivityLogProcessPage = lazy(
  () => import("./pages/AdminActivityLogProcessPage/AdminActivityLogProcessPage"),
)
// ---- Invoices Batches ----
const LazyAdminInvoicesBatchesPage = lazy(
  () => import("./pages/AdminInvoicesBatchesPage/AdminInvoicesBatchesPage"),
)
const LazyAdminInvoicesBatchPage = lazy(
  () => import("./pages/AdminInvoicesBatchPage/AdminInvoicesBatchPage"),
)
const LazyAdminInvoicesBatchIssuesPage = lazy(
  () => import("./pages/AdminInvoicesBatchIssuesPage/AdminInvoicesBatchIssuesPage"),
)

export const staffInvoicingRoutes: OysterRouter = {
  DeprecatedLeftSidebar: [
    {
      path: "invoices",
      children: [
        {
          path: "",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminInvoicesPage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
        {
          path: "new",
          children: [
            {
              path: "",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminCreateInvoicePage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
            {
              path: "contractor/:contractStrongId",
              element: (
                <FlagProtectedRoute flags={{ pcAllowStaffCreatingContractorInvoices: true }}>
                  <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                    <LazyAdminCreateContactorInvoicePage />
                  </LazyLoadingSuspenseWithErrorBoundary>
                </FlagProtectedRoute>
              ),
            },
          ],
        },
        {
          path: ":strongId",
          children: [
            {
              path: "details",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminViewInvoicePage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
            {
              path: "edit",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminEditInvoicePage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
            {
              path: "contractor/edit",
              element: (
                <FlagProtectedRoute flags={{ pcAllowStaffCreatingContractorInvoices: true }}>
                  <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                    <LazyAdminEditContactorInvoicePage />
                  </LazyLoadingSuspenseWithErrorBoundary>
                </FlagProtectedRoute>
              ),
            },
          ],
        },
        {
          path: "load-vendor-data",
          children: [
            {
              path: "",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminInvoicesUploadVendorDataPage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
            {
              path: ":strongId",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminInvoicesUploadVendorDataItemPage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
          ],
        },
        {
          path: "generate-deposit-invoice",
          element: (
            <FlagProtectedRoute flags={{ pcAllowStaffDepositInvoiceGeneration: true }}>
              <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                <LazyAdminGenerateDepositInvoicePage />
              </LazyLoadingSuspenseWithErrorBoundary>
            </FlagProtectedRoute>
          ),
        },
      ],
    },
    {
      path: "invoices-batches",
      children: [
        {
          path: "",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminInvoicesBatchesPage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
        {
          path: ":invoicesBatchStrongId",
          children: [
            {
              path: "",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminInvoicesBatchPage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
            {
              path: "issues",
              element: (
                <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
                  <LazyAdminInvoicesBatchIssuesPage />
                </LazyLoadingSuspenseWithErrorBoundary>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "creditnotes",
      element: <FlagProtectedRoute flags={{ pcAllowStaffManagingCreditNotes: true }} />,
      children: [
        {
          path: "new",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminAddCreditNotePage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
        {
          path: "generate",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminGenerateCreditNotePage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
        {
          path: ":companyStrongId/apply/:strongId",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminApplyCreditNotePage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
      ],
    },
    {
      path: "activity-log",
      children: [
        {
          path: "",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminActivityLogPage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
        {
          path: ":strongId",
          element: (
            <LazyLoadingSuspenseWithErrorBoundary fallback={null}>
              <LazyAdminActivityLogProcessPage />
            </LazyLoadingSuspenseWithErrorBoundary>
          ),
        },
      ],
    },
  ],
  DeprecatedFullScreen: [],
}
